/**
 * Copyright 2021 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2022 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import { IdValueObj, IFair, IFaultyMachine, IMachine, ISoftware } from '@umati/dashboard-types';
import { ISpecification } from 'Specifications';
import { FilterMachineFair, FilterMachineParent, FilterMachineSpecification, IFilterMachineData } from 'utilities/MachinesFilter';
import { IFilterSoftwareData } from 'utilities/SoftwareFilter';
import { initialCommonFilterState } from './reducers/commonFilter';
import { initialMachineFilterState } from './reducers/machineListFilter';
import { initialSoftwareFilterState } from './reducers/softwareFilter';
import { IStore } from './storeType';

const pick = (obj, ...args) => ({
  ...args.reduce((res, key) => ({ ...res, [key]: obj[key] }), {})
});
export function getMachineListFilter (store, fairTag: string): IFilterMachineData {
  if (store.machineListFilter[fairTag]) {
    return store.machineListFilter[fairTag];
  } else {
    return initialMachineFilterState;
  }
}
/// \todo do appropriate merge
export function getCommonListFilter (store, fairTag: string) {
  if (store.commonListFilter[fairTag]) {
    return store.commonListFilter[fairTag];
  } else {
    return initialCommonFilterState;
  }
}
export function getSoftwareListFilter (store, fairTag: string): IFilterSoftwareData {
  if (store.softwareListFilter[fairTag]) {
    return store.softwareListFilter[fairTag];
  } else {
    return initialSoftwareFilterState;
  }
}
export const getMachines = (store: IStore) : IMachine[] => {
  let machines = store.machines?.Machines || [];
  machines = machines?.map(machine => { machine.Favorite = store.machines?.favourites?.includes(machine.MachineId); return machine; });
  return machines;
};
export const getFaultyMachines = (store: IStore) : IFaultyMachine[] => {
  return store.machines?.FaultyMachines || [];
};
export const getMachinesByParent = (store: IStore, parentId: string): IMachine[] => {
  return FilterMachineParent.Filter(getMachines(store), parentId);
};
export const getMachinesByFair = (store: IStore, fair: string): IMachine[] => {
  return FilterMachineFair.Filter(getMachines(store), fair);
};

export const getMachinesByFairAndParent = (store: IStore, fair: string, parent: string): IMachine[] => {
  return FilterMachineParent.Filter(getMachinesByFair(store, fair), parent);
};

export const getMachinesBySpecification = (store: IStore, spec: ISpecification | 'all'): IMachine[] => {
  return FilterMachineSpecification.Filter(getMachines(store), spec);
};

export const getMachine = (store: IStore, machineId): IMachine | undefined => {
  if (store?.machines?.Machines) {
    return store.machines.Machines.filter(machine => machine.MachineId === machineId)[0];
  } else {
    return undefined;
  }
};
export const getFairs = (store: IStore) => typeof store.fairs?.fairs !== 'undefined' ? store.fairs?.fairs : {};
export const getFair = (store: IStore, key): IFair | undefined => store.fairs?.fairs?.[key];
export const getSoftwareList = (store: IStore) => store.softwares?.Softwares;
export const getSoftware = (store: IStore, key) => store.softwares?.Softwares[key];
export const getFlattenedSoftware = (store, key) => {
  const sw = store.softwares?.Softwares?.[key];
  if (sw === undefined) {
    return undefined;
  }
  let strLoc = '';
  Object.keys(sw.Positions).forEach(key => {
    if (sw.Positions[key].hall) {
      strLoc += `${key.toUpperCase()} ${sw.Positions[key].hall} ${sw.Positions[key].booth}/`;
    }
  });
  sw._flatPositions = strLoc.slice(0, -1);
  return sw;
};

/// \todo remove!
/// \deprecate
export const getSoftwareByFair = (store: IStore, fair) => {
  if (store?.softwares?.Softwares) {
    if (fair === 'all') {
      return store.softwares.Softwares;
    }

    const keys = Object.keys(store.softwares.Softwares).filter(key => Object.prototype.hasOwnProperty.call(store.softwares?.Softwares[key].Positions, fair));
    return pick(store.softwares.Softwares, ...keys);
  } else {
    return null;
  }
};

export function getSoftwareByFairArray (store, fairTag: string): ISoftware[] {
  const sw: IdValueObj<ISoftware> | null = getSoftwareByFair(store, fairTag);
  if (!sw) {
    return [];
  }
  return Object.values(sw);
}

export const getFavourites = (store, storeElement) => {
  return store?.[storeElement]?.favourites;
};
